import { setTransform } from "react-grid-layout";
import type { Position, PositionStrategy } from "react-grid-layout/core";
import { RESIZE_OUTLINE_CLASS } from "./grid";
import { GRID_GUTTER_PX } from "./layoutConfig";

/**
 * A resize shows two previews. The card paints only at sizes the grid can
 * store, so its contents reflow once per cell crossed instead of every frame.
 * A bare outline follows the pointer between cells.
 */

/**
 * How far a painted size may sit from a cell boundary and still be left alone.
 * The renderer can leave a settled card a pixel off a boundary, and snapping
 * that pixel would nudge cards nobody is touching.
 */
const SNAP_TOLERANCE_PX = 2;

function snapToQuantum(value: number, quantum: number): number {
    if (!(quantum > 0) || !Number.isFinite(value)) return value;

    const snapped = Math.round(value / quantum) * quantum;
    return Math.abs(snapped - value) <= SNAP_TOLERANCE_PX
        ? value
        : Math.round(snapped);
}

/**
 * The default position strategy, except that a size being resized is painted
 * at the nearest whole column and row. Positions are not snapped, so a moved
 * card still tracks the pointer.
 */
export function createSnappedPositionStrategy(
    columnWidthPx: number,
    rowHeightPx: number,
): PositionStrategy {
    return {
        type: "transform",
        scale: 1,
        calcStyle(position: Position) {
            return setTransform({
                ...position,
                width: snapToQuantum(position.width, columnWidthPx),
                height: snapToQuantum(position.height, rowHeightPx),
            });
        },
    };
}

export type ResizeOutlineLimits = {
    minWidthPx: number;
    maxWidthPx: number;
    minHeightPx: number;
    maxHeightPx: number;
};

export type PointerPosition = { clientX: number; clientY: number };

export type ResizeOutlineController = {
    /**
     * Begins tracking a resize of `card` that started under `pointer`.
     * `getLimits` is called every frame, because the floor rises as a card
     * is narrowed and its content wraps.
     */
    start(
        card: HTMLElement,
        pointer: PointerPosition,
        getLimits: () => ResizeOutlineLimits,
    ): void;
    /** Moves the outline to where the pointer now is. Safe to call per frame. */
    track(pointer: PointerPosition | null): void;
    /** Ends the gesture and removes the outline. */
    stop(): void;
};

const clamp = (value: number, lowest: number, highest: number) =>
    Math.min(Math.max(value, lowest), highest);

/** The pointer position from a `MouseEvent` or `TouchEvent`. */
export function getPointerPosition(event: Event): PointerPosition | null {
    if ("clientX" in event && typeof event.clientX === "number") {
        return {
            clientX: event.clientX,
            clientY: (event as MouseEvent).clientY,
        };
    }

    const touch = (event as TouchEvent).touches?.[0];
    return touch ? { clientX: touch.clientX, clientY: touch.clientY } : null;
}

/**
 * Draws the pointer-following half of a resize. Imperative, because routing a
 * per-frame update through React would re-render the grid.
 */
export function createResizeOutlineController(): ResizeOutlineController {
    let card: HTMLElement | null = null;
    let outline: HTMLElement | null = null;
    let readLimits: (() => ResizeOutlineLimits) | null = null;
    /** Pointer offset from the card's bottom-right corner when grabbed. */
    let grabOffsetX = 0;
    let grabOffsetY = 0;
    let pending: PointerPosition | null = null;
    let frame = 0;

    const draw = () => {
        frame = 0;
        if (!outline || !card || !readLimits || !pending) return;

        const limits = readLimits();

        // Measured from the card's current corner each frame, so scrolling
        // or the page growing mid-resize cannot make the outline drift.
        const rect = card.getBoundingClientRect();
        const width = clamp(
            pending.clientX + grabOffsetX - rect.left,
            limits.minWidthPx,
            limits.maxWidthPx,
        );
        const height = clamp(
            pending.clientY + grabOffsetY - rect.top,
            limits.minHeightPx,
            limits.maxHeightPx,
        );

        // Inset by the gutter so the outline sits on the card's own edges.
        const inset = GRID_GUTTER_PX;
        outline.style.left = `${inset}px`;
        outline.style.top = `${inset}px`;
        outline.style.width = `${Math.max(0, width - inset * 2)}px`;
        outline.style.height = `${Math.max(0, height - inset * 2)}px`;
    };

    const remove = () => {
        outline?.remove();
        outline = null;
        card = null;
        readLimits = null;
        pending = null;
    };

    return {
        start(nextCard, pointer, getLimits) {
            remove();
            window.cancelAnimationFrame(frame);
            frame = 0;

            const rect = nextCard.getBoundingClientRect();
            card = nextCard;
            readLimits = getLimits;
            grabOffsetX = rect.right - pointer.clientX;
            grabOffsetY = rect.bottom - pointer.clientY;

            outline = document.createElement("div");
            outline.className = RESIZE_OUTLINE_CLASS;
            outline.setAttribute("aria-hidden", "true");
            outline.style.position = "absolute";
            outline.style.pointerEvents = "none";
            nextCard.append(outline);

            pending = pointer;
            draw();
        },

        track(pointer) {
            if (!outline || !pointer) return;

            pending = pointer;
            if (frame !== 0) return;
            frame = window.requestAnimationFrame(draw);
        },

        stop() {
            window.cancelAnimationFrame(frame);
            frame = 0;
            remove();
        },
    };
}

/**
 * The empty grid as a background image: one SVG tile a full row wide with a
 * rounded rectangle per column, repeated vertically. A row-wide tile keeps
 * fractional column widths from drifting across the row.
 */
export function createGridBackgroundImage({
    containerWidthPx,
    columns,
    rowHeightPx,
    insetPx,
    radiusPx,
    color,
}: {
    containerWidthPx: number;
    columns: number;
    rowHeightPx: number;
    insetPx: number;
    radiusPx: number;
    color: string;
}): string | undefined {
    if (!(containerWidthPx > 0) || !(rowHeightPx > 0) || columns < 1) {
        return undefined;
    }

    const columnWidth = containerWidthPx / columns;
    const cellWidth = columnWidth - insetPx * 2;
    const cellHeight = rowHeightPx - insetPx * 2;
    if (cellWidth <= 0 || cellHeight <= 0) return undefined;

    const cells = Array.from(
        { length: columns },
        (_unused, column) =>
            `<rect x="${(column * columnWidth + insetPx).toFixed(
                2,
            )}" y="${insetPx}" ` +
            `width="${cellWidth.toFixed(2)}" height="${cellHeight}" ` +
            `rx="${radiusPx}" fill="${color}"/>`,
    ).join("");

    const svg =
        `<svg xmlns="http://www.w3.org/2000/svg" ` +
        `width="${containerWidthPx}" height="${rowHeightPx}">${cells}</svg>`;

    return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}
